import { API_BASE_URL, getAuthHeaders } from './utils';

export const MIN_HOLD_DAYS = 1;
export const MAX_HOLD_DAYS = 30;

export const toDateOnly = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const date = new Date(value.getTime());
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const holdDurationDays = (startDate, endDate) => {
  const start = toDateOnly(startDate);
  const end = toDateOnly(endDate);
  return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
};

export const countHoldDaysOverlapping = (
  fromDate,
  toDate,
  intervals = []
) => {
  const from = toDateOnly(fromDate);
  const to = toDateOnly(toDate);
  if (from > to) {
    return 0;
  }

  return (intervals || []).reduce((total, interval) => {
    const start = toDateOnly(interval.start_date || interval.startDate);
    const end = toDateOnly(interval.end_date || interval.endDate);
    const overlapStart = start > from ? start : from;
    const overlapEnd = end < to ? end : to;
    if (overlapStart > overlapEnd) {
      return total;
    }
    return (
      total +
      Math.round((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24)) +
      1
    );
  }, 0);
};

export const holdIntervalsFromRecords = (holds = []) => {
  const seen = new Set();
  return (holds || [])
    .filter(
      (hold) =>
        hold &&
        hold.status !== 'cancelled' &&
        (hold.start_date || hold.startDate) &&
        (hold.end_date || hold.endDate)
    )
    .map((hold) => ({
      start_date: hold.start_date || hold.startDate,
      end_date: hold.end_date || hold.endDate,
    }))
    .filter((interval) => {
      const key = `${interval.start_date}:${interval.end_date}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const getStoredHoldIntervals = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || 'null');
    const holds = Array.isArray(user?.progress_holds) ? user.progress_holds : [];
    const current = user?.progress_hold ? [user.progress_hold] : [];
    return holdIntervalsFromRecords([...holds, ...current]);
  } catch {
    return [];
  }
};

let journeyHoldRequest = null;

export const loadJourneyHoldIntervals = async () => {
  if (!journeyHoldRequest) {
    journeyHoldRequest = fetchProgressHolds()
      .then((payload) => {
        persistProgressHoldsInSession(payload);
        return holdIntervalsFromRecords(payload?.holds);
      })
      .catch((error) => {
        console.error('Unable to refresh English journey pauses:', error);
        return getStoredHoldIntervals();
      })
      .finally(() => {
        journeyHoldRequest = null;
      });
  }
  return journeyHoldRequest;
};

export const getActiveProgressHold = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || 'null');
    const hold = user?.progress_hold;
    if (!hold) {
      return null;
    }
    if (hold.status === 'cancelled' || hold.status === 'completed') {
      return null;
    }
    return hold;
  } catch {
    return null;
  }
};

const parseApiError = async (response, fallbackMessage) => {
  try {
    const errorData = await response.json();
    return errorData?.detail || errorData?.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

const callProgressHoldApi = async (endpoint, options = {}) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    throw new Error(
      await parseApiError(response, 'Unable to update progress hold.')
    );
  }

  return response.json();
};

export const fetchProgressHolds = () =>
  callProgressHoldApi('/api/progress-holds', { method: 'GET' });

export const createProgressHold = ({ startDate, endDate, reason }) =>
  callProgressHoldApi('/api/progress-holds', {
    method: 'POST',
    body: JSON.stringify({
      start_date: startDate,
      end_date: endDate,
      reason: reason || null,
    }),
  });

export const updateProgressHold = (holdId, { startDate, endDate, reason }) =>
  callProgressHoldApi(`/api/progress-holds/${holdId}`, {
    method: 'PATCH',
    body: JSON.stringify({
      start_date: startDate || null,
      end_date: endDate || null,
      reason: reason,
    }),
  });

export const cancelProgressHold = (holdId) =>
  callProgressHoldApi(`/api/progress-holds/${holdId}`, {
    method: 'DELETE',
  });

export const persistProgressHoldsInSession = (payload) => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || 'null');
    if (!user) {
      return;
    }
    const nextUser = {
      ...user,
      progress_hold: payload?.current || null,
      progress_holds: payload?.holds || [],
    };
    sessionStorage.setItem('user', JSON.stringify(nextUser));
  } catch {
    // Session storage is best-effort for banners.
  }
};
